import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import { IMAGE_ACTION } from 'Pods/ThreeDStudio/Components/ImageAssistantPanel/imageModels'
import {
  getCurrentSessionId, selectSessionId, subscribeToGeneratorRun, VERSION_KIND,
} from 'Pods/ThreeDStudio/Components/ImageAssistantPanel/imageRun'
import { MAX_ATTACHMENTS } from 'Pods/ThreeDStudio/constants'
import useImageAssetUpload from 'Pods/ThreeDStudio/Hooks/useImageAssetUpload'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { setInputImage } from 'Pods/ThreeDStudio/Store/actions/modelSettings'
import { closeImageAssistant, exitImageEditor, setImageAssistantSessionId } from 'Pods/ThreeDStudio/Store/actions/ui'
import { IMAGE_ASSISTANT_TAB } from 'Pods/ThreeDStudio/Store/constants'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import { buildVersionChain } from './buildVersionChain'
import useMaskCanvas from './useMaskCanvas'
import useSessionImageGenerations from '../../../CreateTab/useSessionImageGenerations'

/**
 * Owns the Edit canvas state: the image loaded onto the mask canvas, the composer
 * (prompt + reference attachments), the generated-version history, and the History
 * dropdown. Composes useMaskCanvas for the drawing tools. A painted mask runs a
 * masked edit (base image + red-overlay marker); an instruction alone runs a
 * single-image prompt edit.
 */
export default function useEditCanvas() {
  const t = useTranslation()
  const editImage = useThreeDStudioStore(state => state.ui.editImage)
  const activeTab = useThreeDStudioStore(state => state.ui.imageAssistantTab)
  const sessionId = useThreeDStudioStore(selectSessionId)
  const { generations } = useSessionImageGenerations(sessionId)
  const canvas = useMaskCanvas()

  const [currentImage, setCurrentImage] = useState(editImage)
  const [versions, setVersions] = useState(EMPTY_ARRAY)
  // The image nothing was edited from. The handed-in image until a lineage is restored,
  // which may reveal that it is itself an edit of something earlier.
  const [original, setOriginal] = useState(editImage)
  const [prompt, setPrompt] = useState('')
  const [attachments, setAttachments] = useState(EMPTY_ARRAY)
  const [isHistoryOpen, setIsHistoryOpen] = useState(false)
  const [isSending, setIsSending] = useState(false)

  const fileInputRef = useRef(null)
  const idRef = useRef(0)
  // The loaded image is Version 1, so the first edit of it is Version 2.
  const versionCountRef = useRef(editImage ? 1 : 0)
  const isSendingRef = useRef(false)
  const isMountedRef = useRef(true)
  const subscriptionsRef = useRef(new Set())
  const restoredRef = useRef(false)

  useEffect(() => () => {
    isMountedRef.current = false
    subscriptionsRef.current.forEach(subscription => subscription.unsubscribe())
    subscriptionsRef.current.clear()
  }, [])

  const { postData } = useApiPost()
  const uploadImageAsset = useImageAssetUpload()

  const nextId = () => {
    idRef.current += 1
    return idRef.current
  }

  const clearAttachments = useCallback(() => {
    setAttachments((prev) => {
      if (prev.length === 0) {
        return prev
      }
      prev.forEach(item => URL.revokeObjectURL(item.url))

      return EMPTY_ARRAY
    })
  }, [])

  // A masked edit sends only the base image and the overlay, so the upload control is
  // hidden here; entering the tab also drops anything already attached rather than
  // leaving chips that would never be sent.
  useEffect(() => {
    if (activeTab === IMAGE_ASSISTANT_TAB.EDIT) {
      clearAttachments()
    }
  }, [activeTab, clearAttachments])

  // The whole lineage this image belongs to is put back in the History dropdown, so
  // applying a middle version and reopening still shows every version around it rather
  // than just what came after. currentImage is deliberately left as the image that was
  // handed in: the tab is only ever entered by picking an image (closeImageAssistant
  // clears editImage), and loading a different one would open something the user did not
  // click. The Toolbar highlights it by id, and selecting another version switches to it.
  useEffect(() => {
    if (restoredRef.current || !editImage) {
      return
    }

    const lineage = buildVersionChain(generations, editImage.id)
    if (lineage.length === 0) {
      return
    }
    restoredRef.current = true
    // Keeps new labels from colliding with the restored ones.
    versionCountRef.current = lineage.length
    // The lineage starts with the image nothing produced, which is the original; the
    // edits after it are Version 2 onwards, held newest first as applyVersion prepends.
    const [originalAsset, ...edits] = lineage
    setOriginal(originalAsset)
    setVersions(edits
      .map((version, index) => ({
        ...version,
        label: t('imageAssistantPanel.editTab.toolbar.version', { number: index + 2 }),
      }))
      .reverse())
    // The handed-in image stays loaded, but the lineage knows how it was made — carry
    // that over so the prompt viewer can show the instruction that produced it.
    const loadedEntry = lineage.find(version => String(version.id) === String(editImage.id))
    if (loadedEntry?.prompt) {
      setCurrentImage(prev => ({ ...prev, kind: loadedEntry.kind, prompt: loadedEntry.prompt }))
    }
  }, [generations, editImage, t])

  const handlePromptChange = useCallback(event => setPrompt(event.target.value), [])

  const handleUploadClick = useCallback(() => fileInputRef.current?.click(), [])

  const handleRemoveAttachment = useCallback((id) => {
    setAttachments((prev) => {
      const removed = prev.find(image => image.id === id)
      if (removed) {
        URL.revokeObjectURL(removed.url)
      }
      return prev.filter(image => image.id !== id)
    })
  }, [])

  const handleFilesChange = useCallback((event) => {
    const picked = Array.from(event.target.files || [])
    const remaining = MAX_ATTACHMENTS - attachments.length
    if (picked.length && remaining > 0) {
      const added = picked
        .slice(0, remaining)
        .map(file => ({ id: nextId(), url: URL.createObjectURL(file), file }))
      setAttachments(prev => [...prev, ...added])
    }
    // Reset so picking the same file again still fires change.
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [attachments])

  const stopSending = useCallback(() => {
    if (!isMountedRef.current) {
      return
    }
    isSendingRef.current = false
    setIsSending(false)
  }, [])

  const failEdit = useCallback(() => {
    stopSending()
    if (typeof window !== 'undefined' && window.showNotification) {
      window.showNotification('Edit failed. Please try again.', { type: 'error', autohide: true })
    }
  }, [stopSending])

  const applyVersion = useCallback((asset, meta) => {
    if (!isMountedRef.current) {
      return
    }
    versionCountRef.current += 1
    // A live edit owns the history from here on, so a late-arriving restore is skipped.
    restoredRef.current = true
    const version = {
      id: asset.assetId,
      url: asset.fileUrl,
      label: t('imageAssistantPanel.editTab.toolbar.version', { number: versionCountRef.current }),
      kind: meta?.kind,
      prompt: meta?.prompt,
    }
    // Newest first so the History dropdown lists the latest version on top.
    setVersions(prev => [version, ...prev])
    setCurrentImage(version)
    setPrompt('')
    clearAttachments()
    canvas.clear()
    stopSending()
  }, [canvas, clearAttachments, stopSending])

  // Wait for the run's websocket terminal update, then add the edited image as a new
  // version (or surface a failure). meta records how the version was made (kind) and
  // with which instruction (prompt) for the history subtitle and the prompt viewer.
  const subscribeToRun = useCallback((runId, meta) => {
    const subscription = subscribeToGeneratorRun(runId, {
      onImage: (asset) => {
        subscriptionsRef.current.delete(subscription)
        applyVersion(asset, meta)
      },
      onFail: () => {
        subscriptionsRef.current.delete(subscription)
        failEdit()
      },
    })

    if (subscription) {
      subscriptionsRef.current.add(subscription)
    } else {
      failEdit()
    }
  }, [applyVersion, failEdit])

  // Send needs an edit instruction and a loaded image. With a painted mask it uploads
  // the red-overlay marker and starts a masked-edit run: input_image is the base image
  // (Image 1) and the overlay (Image 2), ordered by asset position. Without one it
  // starts an image-edit run on the base image alone, applying the instruction verbatim.
  //
  // The action goes on use_case rather than a model_id, so the backend ranks the providers
  // offering it instead of the run being locked to one. Both edit actions cap input_image
  // (masked-edit exactly 2, image-edit exactly 1) in both catalogs, so reference
  // attachments are not part of the payload - an extra image is rejected.
  const handleSend = useCallback(() => {
    const editText = prompt.trim()
    if (isSendingRef.current || !editText || !currentImage) {
      return
    }
    isSendingRef.current = true
    setIsSending(true)

    const baseAssetId = currentImage.id
    const meta = {
      kind: canvas.hasPainted ? VERSION_KIND.BRUSH : VERSION_KIND.PROMPT,
      prompt: editText,
    }

    const startRun = (useCase, inputImage, runSessionId) => {
      postData({
        path: '/api/internal/generator/runs',
        data: {
          data: {
            run_type: 'image_generation',
            use_case: useCase,
            ...(runSessionId ? { generator_session_id: runSessionId } : {}),
            canonical_params: { prompt: editText, input_image: inputImage },
          },
        },
        onSuccess: (response) => {
          const { id, generatorSessionId } = response.data.data.attributes
          if (generatorSessionId) {
            setImageAssistantSessionId(generatorSessionId)
          }
          subscribeToRun(id, meta)
        },
        onError: () => failEdit(),
      })
    }

    if (!canvas.hasPainted) {
      startRun(IMAGE_ACTION.IMAGE_EDIT, [{ asset_id: baseAssetId }], getCurrentSessionId())
      return
    }

    canvas.toOverlayFile(currentImage.url)
      .then((overlayFile) => {
        if (!overlayFile) {
          throw new Error('Failed to build mask overlay')
        }
        // Read at send time rather than reusing the rendered sessionId, so a session
        // created moments ago by the first run is picked up.
        const runSessionId = getCurrentSessionId()

        return uploadImageAsset(overlayFile, { sessionId: runSessionId })
          .then(maskAssetId => ({ maskAssetId, runSessionId }))
      })
      .then(({ maskAssetId, runSessionId }) => {
        startRun(IMAGE_ACTION.MASKED_EDIT, [{ asset_id: baseAssetId }, { asset_id: maskAssetId }], runSessionId)
      })
      .catch(() => failEdit())
  }, [canvas, prompt, currentImage, postData, uploadImageAsset, subscribeToRun, failEdit])

  const handleToggleHistory = useCallback(() => setIsHistoryOpen(prev => !prev), [])

  const handleCloseHistory = useCallback(() => setIsHistoryOpen(false), [])

  const handleSelectVersion = useCallback((version) => {
    setCurrentImage(version)
    setIsHistoryOpen(false)
    canvas.clear()
  }, [canvas])

  // The version is already a Generator::Asset, so reuse its id as the 3D input rather
  // than re-uploading the file.
  const handleApply = useCallback(() => {
    if (!currentImage) {
      return
    }
    setInputImage(currentImage.id, currentImage.url)
    closeImageAssistant()
  }, [currentImage])

  // Keep the latest attachments in a ref so the unmount cleanup revokes the
  // current object URLs without re-running (and revoking still-rendered URLs) on
  // every attachment change. Removal and send already revoke their own URLs.
  const attachmentsRef = useRef(attachments)
  attachmentsRef.current = attachments

  useEffect(() => () => {
    attachmentsRef.current.forEach(item => URL.revokeObjectURL(item.url))
  }, [])

  // The original closes out the history as Version 1, so the user can always get back to
  // it. Edits are held newest first, so it goes last.
  const historyVersions = useMemo(() => {
    if (!original) {
      return versions
    }

    return [...versions, {
      id: original.id,
      url: original.url,
      label: t('imageAssistantPanel.editTab.toolbar.originalVersion'),
    }]
  }, [versions, original, t])

  return {
    canvas,
    editImage,
    currentImage,
    versions: historyVersions,
    prompt,
    attachments,
    isHistoryOpen,
    isSending,
    isSendDisabled: isSending || !prompt.trim(),
    isUploadDisabled: attachments.length >= MAX_ATTACHMENTS,
    fileInputRef,
    handlePromptChange,
    handleUploadClick,
    handleRemoveAttachment,
    handleFilesChange,
    handleSend,
    handleToggleHistory,
    handleCloseHistory,
    handleSelectVersion,
    handleApply,
    handleExit: exitImageEditor,
  }
}
